import React, { useEffect, useRef, useState } from 'react';
import './ScrollAnimation.css';

const FRAME_COUNT = 240;

const ScrollAnimation = () => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [images, setImages] = useState([]);
    const [loadedFrames, setLoadedFrames] = useState(0);

    useEffect(() => {
        const loadedImages = [];
        let loadedCount = 0;
        for (let i = 1; i <= FRAME_COUNT; i++) {
            const img = new Image();
            const frameNum = i.toString().padStart(3, '0');
            img.src = `/dhwani-header/ezgif-frame-${frameNum}.jpg`;
            img.onload = () => {
                loadedCount++;
                setLoadedFrames(loadedCount);
            };
            loadedImages.push(img);
        }
        setImages(loadedImages);
    }, []);

    useEffect(() => {
        if (images.length === 0) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext('2d');

        // Draw the first frame once it and the canvas are ready
        if (images[0] && images[0].naturalWidth > 0) {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            drawImageProp(context, images[0], canvas.width, canvas.height);
        }

        const handleScroll = () => {
            if (!containerRef.current) return;
            const html = document.documentElement;

            const rect = containerRef.current.getBoundingClientRect();
            const containerTop = rect.top + window.scrollY;
            const containerHeight = containerRef.current.offsetHeight;

            const scrollPosition = html.scrollTop - containerTop;
            const maxScroll = containerHeight - window.innerHeight;

            let frameIndex = 0;
            if (scrollPosition > maxScroll) {
                frameIndex = FRAME_COUNT - 1;
            } else if (scrollPosition > 0) {
                const scrollFraction = scrollPosition / maxScroll;
                frameIndex = Math.min(
                    FRAME_COUNT - 1,
                    Math.floor(scrollFraction * FRAME_COUNT)
                );
            }

            requestAnimationFrame(() => {
                const img = images[frameIndex];
                if (img && img.complete && img.naturalWidth > 0) {
                    canvas.width = window.innerWidth;
                    canvas.height = window.innerHeight;
                    drawImageProp(context, img, canvas.width, canvas.height);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [images, loadedFrames]);

    return (
        <div className="scroll-animation-container" ref={containerRef} id="dhwani-animation">
            <div className="canvas-wrapper">
                <canvas ref={canvasRef} />
                {loadedFrames < FRAME_COUNT && (
                    <div className="loading-indicator">
                        Loading animation... {Math.floor((loadedFrames / FRAME_COUNT) * 100)}%
                    </div>
                )}
            </div>
        </div>
    );
};
function drawImageProp(ctx, img, canvasWidth, canvasHeight) {
    // Clear the canvas first
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Fill with black background
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Target maximum dimensions as requested by user
    const maxWidth = 828;
    const maxHeight = 540;

    let finalWidth = maxWidth;
    let finalHeight = maxHeight;

    // Responsive scaling for mobile devices
    if (canvasWidth < maxWidth) {
        // Leave a little margin on mobile (e.g., 90% of screen width)
        const scale = (canvasWidth * 0.9) / maxWidth;
        finalWidth = maxWidth * scale;
        finalHeight = maxHeight * scale;
    }

    // Center the image on the screen canvas
    let x = (canvasWidth - finalWidth) / 2;
    let y = (canvasHeight - finalHeight) / 2;

    ctx.drawImage(img, x, y, finalWidth, finalHeight);
}

export default ScrollAnimation;
